import { nip19 } from 'nostr-tools';
import { NSchema as n } from '@nostrify/nostrify';
import type { NostrEvent, NostrMetadata, NPool } from '@nostrify/nostrify';
import type {
  ActivitySignals,
  EngagementResult,
  EngagementSettings,
  EngagementAction,
  Participant,
} from './tombolo';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

interface RelayLike {
  query(filters: object[], opts?: { signal?: AbortSignal }): Promise<NostrEvent[]>;
}

interface PoolLike {
  relay(url: string): RelayLike;
  group(urls: string[]): RelayLike;
}

// ---------------------------------------------------------------------------
// Step 3: fetch engagement from each relay individually so we can report
// per-relay failures while continuing with the rest.
// ---------------------------------------------------------------------------

export async function fetchEngagement(
  pool: NPool,
  relayUrls: string[],
  noteIds: string[],
  settings: EngagementSettings,
  onProgress?: (message: string) => void,
): Promise<EngagementResult> {
  const kinds: number[] = [];
  if (settings.likes) kinds.push(7);
  if (settings.reposts) kinds.push(6, 16);
  if (settings.comments) kinds.push(1, 1111);

  const eventsById = new Map<string, NostrEvent>();
  const relaysByEventId = new Map<string, Set<string>>();
  const okRelays: string[] = [];
  const failedRelays: string[] = [];

  // Query each relay separately (parallel) so one bad relay never sinks the fetch.
  await Promise.all(
    relayUrls.map(async (url) => {
      try {
        const relay = (pool as unknown as PoolLike).relay(url);
        const events = await relay.query(
          [{ kinds, '#e': noteIds, limit: 3000 }],
          { signal: AbortSignal.timeout(12_000) },
        );
        okRelays.push(url);
        onProgress?.(`Got ${events.length} events from ${url}`);
        for (const ev of events) {
          eventsById.set(ev.id, ev);
          if (!relaysByEventId.has(ev.id)) relaysByEventId.set(ev.id, new Set());
          relaysByEventId.get(ev.id)!.add(url);
        }
      } catch {
        failedRelays.push(url);
        onProgress?.(`Relay failed: ${url}`);
      }
    }),
  );

  const noteIdSet = new Set(noteIds);
  const participants = new Map<string, Participant>();
  const totals = { likes: 0, reposts: 0, comments: 0, rawEvents: 0 };
  const engagementEventIds: string[] = [];

  const classify = (ev: NostrEvent): EngagementAction | null => {
    switch (ev.kind) {
      case 7:
        // Skip explicit downvotes
        return ev.content.trim() === '-' ? null : 'like';
      case 6:
      case 16:
        return 'repost';
      case 1:
      case 1111:
        return 'comment';
      default:
        return null;
    }
  };

  for (const ev of eventsById.values()) {
    // Verify the event actually references one of our notes
    const referencedNotes = ev.tags
      .filter(([name, value]) => name === 'e' && noteIdSet.has(value))
      .map(([, value]) => value);
    if (referencedNotes.length === 0) continue;

    const action = classify(ev);
    if (!action) continue;

    totals.rawEvents++;
    if (action === 'like') totals.likes++;
    else if (action === 'repost') totals.reposts++;
    else totals.comments++;
    engagementEventIds.push(ev.id);

    let p = participants.get(ev.pubkey);
    if (!p) {
      p = {
        pubkey: ev.pubkey,
        npub: nip19.npubEncode(ev.pubkey),
        actions: [],
        noteIds: [],
        relays: [],
      };
      participants.set(ev.pubkey, p);
    }
    if (!p.actions.includes(action)) p.actions.push(action);
    for (const noteId of referencedNotes) {
      if (!p.noteIds.includes(noteId)) p.noteIds.push(noteId);
    }
    for (const relayUrl of relaysByEventId.get(ev.id) ?? []) {
      if (!p.relays.includes(relayUrl)) p.relays.push(relayUrl);
    }
  }

  // Fetch profile metadata (kind 0) for everyone found, from the same wide
  // relay set — profiles are often missing from a user's default relays.
  onProgress?.('Loading profiles…');
  const pubkeys = [...participants.keys()];
  const profiles: Record<string, NostrMetadata> = {};
  const group = (pool as unknown as PoolLike).group(okRelays.length > 0 ? okRelays : relayUrls);

  for (const batch of chunk(pubkeys, 100)) {
    try {
      const events = await group.query(
        [{ kinds: [0], authors: batch, limit: batch.length }],
        { signal: AbortSignal.timeout(10_000) },
      );
      for (const ev of events) {
        try {
          profiles[ev.pubkey] = n.json().pipe(n.metadata()).parse(ev.content);
        } catch {
          // unparseable profile — treat as missing
        }
      }
    } catch {
      // profile fetch failure is non-fatal
    }
  }

  return {
    participants: [...participants.values()],
    totals,
    okRelays,
    failedRelays,
    profiles,
    engagementEventIds,
    notesScanned: noteIds.length,
  };
}

// ---------------------------------------------------------------------------
// Step 5: gather activity / web-of-trust signals for the anti-bot filter
// ---------------------------------------------------------------------------

export async function fetchTrustSignals(
  pool: NPool,
  relayUrls: string[],
  pubkeys: string[],
  engagementEventIds: string[],
  seedPubkeys: string[],
  onProgress?: (message: string) => void,
): Promise<Record<string, ActivitySignals>> {
  // Query the same wide relay set used for the engagement scan, so trust
  // signals (follows, activity) aren't missed just because they live on
  // a different relay.
  const group = (pool as unknown as PoolLike).group(relayUrls);
  const signals: Record<string, ActivitySignals> = {};
  for (const pk of pubkeys) {
    signals[pk] = {
      hasFollows: false,
      followCount: 0,
      hasFollowers: false,
      activityCount: 0,
      oldestEventAt: undefined,
      followedBySeed: false,
    };
  }

  const engagementIdSet = new Set(engagementEventIds);
  const pubkeySet = new Set(pubkeys);
  const batches = chunk(pubkeys, 50);

  // 1) Seed accounts' follow lists (who the trusted seeds follow)
  const seedFollows = new Set<string>();
  if (seedPubkeys.length > 0) {
    onProgress?.('Checking trusted seed accounts…');
    try {
      const seedLists = await group.query(
        [{ kinds: [3], authors: seedPubkeys, limit: seedPubkeys.length }],
        { signal: AbortSignal.timeout(10_000) },
      );
      for (const ev of seedLists) {
        for (const [name, value] of ev.tags) {
          if (name === 'p') seedFollows.add(value);
        }
      }
    } catch {
      // non-fatal
    }
  }

  let done = 0;
  for (const batch of batches) {
    onProgress?.(`Checking activity… (${done}/${pubkeys.length} people)`);
    try {
      const [followLists, followerEvents, activityEvents] = await Promise.all([
        // Their own follow lists
        group.query(
          [{ kinds: [3], authors: batch, limit: batch.length }],
          { signal: AbortSignal.timeout(10_000) },
        ).catch(() => [] as NostrEvent[]),
        // Anyone who follows them
        group.query(
          [{ kinds: [3], '#p': batch, limit: 500 }],
          { signal: AbortSignal.timeout(10_000) },
        ).catch(() => [] as NostrEvent[]),
        // Their general activity (sample)
        group.query(
          [{ authors: batch, limit: 1000 }],
          { signal: AbortSignal.timeout(12_000) },
        ).catch(() => [] as NostrEvent[]),
      ]);

      for (const ev of followLists) {
        const s = signals[ev.pubkey];
        if (!s) continue;
        const count = ev.tags.filter(([name]) => name === 'p').length;
        if (count > s.followCount) {
          s.followCount = count;
          s.hasFollows = count > 0;
        }
      }

      for (const ev of followerEvents) {
        // The author of this contact list follows some of our participants
        for (const [name, value] of ev.tags) {
          if (name === 'p' && pubkeySet.has(value) && ev.pubkey !== value) {
            signals[value].hasFollowers = true;
          }
        }
      }

      for (const ev of activityEvents) {
        const s = signals[ev.pubkey];
        if (!s) continue;
        if (!engagementIdSet.has(ev.id)) {
          s.activityCount++;
        }
        if (s.oldestEventAt === undefined || ev.created_at < s.oldestEventAt) {
          s.oldestEventAt = ev.created_at;
        }
      }
    } catch {
      // keep going with the next batch
    }
    done += batch.length;
  }

  // Apply seed follows
  for (const pk of pubkeys) {
    if (seedFollows.has(pk)) signals[pk].followedBySeed = true;
  }

  return signals;
}
